import { Injectable, OnDestroy, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, ReplaySubject, Subscription, firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { authConfig, createAuthConfig, USE_LIBRARY_SILENT_REFRESH } from '@auth/auth.config';
import { setCookie, deleteCookie, resolveCookieDomain } from '@core/utils/cookie.utils';
import { EnvironmentService } from '@core/services/environment.service';
import { AddressNumberService } from '@services/address-number.service';
import { CustomerService } from '@services/customer.service';
import { ClaimsService } from '@services/claims.service';

/** Threshold in minutes before access token expiration to trigger silent refresh */
const TOKEN_REFRESH_THRESHOLD_MIN = 2;
const COOKIE_NAME = 'we_ah';

@Injectable({
	providedIn: 'root',
})
export class AuthService implements OnDestroy {
	private readonly oauthService = inject(OAuthService);
	private readonly environmentService = inject(EnvironmentService);
	private readonly addressNumberService = inject(AddressNumberService);
	private readonly customerService = inject(CustomerService);
	private readonly claimsService = inject(ClaimsService);

	public authenticationEventObservable: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
	private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
	public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

	private oauthEventsSubscription: Subscription | null = null;
	private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private visibilityHandler: (() => void) | null = null;

	constructor() {
		void this.initializeAuth();
	}

	ngOnDestroy(): void {
		this.stopSessionMonitoring();
		this.oauthEventsSubscription?.unsubscribe();
	}

	// ──────────────────────────────────────────────
	// Initialization
	// ──────────────────────────────────────────────

	private async initializeAuth(): Promise<void> {
		try {
			await firstValueFrom(this.environmentService.ready().pipe(first()));
			this.configureOAuth();
		} catch (error) {
			console.error('🔄 Auth: Failed to initialize', error);
		}
	}

	private configureOAuth(): void {
		if (!this.environmentService.isInitialized) {
			this.oauthService.configure(authConfig);
		} else {
			this.oauthService.configure(createAuthConfig(this.environmentService));
		}

		// Save the configured logoutUrl before discovery document overwrites it
		const configuredLogoutUrl = this.oauthService.logoutUrl;

		this.oauthService
			.loadDiscoveryDocumentAndTryLogin()
			.then(() => {
				// Restore logoutUrl (discovery document overwrites it with end_session_endpoint)
				if (configuredLogoutUrl) {
					this.oauthService.logoutUrl = configuredLogoutUrl;
				}

				const hasValidToken = this.oauthService.hasValidAccessToken();
				this.isAuthenticatedSubject.next(hasValidToken);
				this.authenticationEventObservable.next(hasValidToken);

				if (hasValidToken) {
					this.setAuthHintCookie();
					this.startSessionMonitoring();
				} else {
					console.log('🔄 OAuth: Not authenticated or token invalid');
				}
			})
			.catch((error) => {
				console.error('🔄 OAuth: Error loading discovery document', error);
			});

		// Subscribe to OAuth events
		this.oauthEventsSubscription = this.oauthService.events.subscribe((event) => {
			if (event.type === 'token_received') {
				console.log('🔄 OAuth: Token received');
				// Update auth state
				const valid = this.oauthService.hasValidAccessToken();
				this.isAuthenticatedSubject.next(valid);
				this.authenticationEventObservable.next(valid);

				if (valid) {
					this.setAuthHintCookie();
				}

				// Re-schedule next refresh based on new token
				this.scheduleTokenRefresh();

				// Clean up OAuth callback URL params
				const urlParams = new URLSearchParams(window.location.search);
				if (urlParams.has('code')) {
					const cleanUrl = window.location.origin + window.location.pathname;
					window.history.replaceState({}, document.title, cleanUrl);
				}
			}

			if (event.type === 'token_expires') {
				// Ignore — our scheduleTokenRefresh() handles access token refresh precisely.
				// This event also fires for the short-lived ID token (~5 min) which we don't need to react to.
			}

			if (event.type === 'silent_refresh_error') {
				console.warn('🔄 OAuth: Silent refresh failed — user may need to log in again');
			}
		});
	}

	// ──────────────────────────────────────────────
	// Session monitoring (no polling, no HTTP calls)
	// ──────────────────────────────────────────────

	/**
	 * Start monitoring session using:
	 * 1. Visibility API — validate token when user returns to tab
	 * 2. Scheduled timeout — trigger refresh before access token expires
	 */
	private startSessionMonitoring(): void {
		// 1. Visibility API: check token when user comes back to tab
		this.visibilityHandler = () => {
			if (document.visibilityState === 'visible') {
				this.validateTokenLocally();
			}
		};
		document.addEventListener('visibilitychange', this.visibilityHandler);

		// 2. Schedule a refresh based on access token expiration
		this.scheduleTokenRefresh();
	}

	private stopSessionMonitoring(): void {
		// Clean up visibility listener
		if (this.visibilityHandler) {
			document.removeEventListener('visibilitychange', this.visibilityHandler);
			this.visibilityHandler = null;
		}
		// Clean up scheduled refresh
		if (this.refreshTimeoutId !== null) {
			clearTimeout(this.refreshTimeoutId);
			this.refreshTimeoutId = null;
		}
	}

	/**
	 * Validate token locally (no HTTP call). Called on visibility change.
	 */
	private validateTokenLocally(): void {
		if (!this.oauthService.hasValidAccessToken()) {
			console.log('🔄 Session: Access token invalid after returning to tab');
			this.handleExternalLogout();
			return;
		}

		// If close to expiring, attempt refresh
		if (this.isTokenCloseToExpiring(TOKEN_REFRESH_THRESHOLD_MIN)) {
			const info = this.getTokenExpirationInfo();
			console.log(`🔄 Session: Token expires in ${info.remainingMinutes} min, refreshing`);
			this.attemptSilentTokenRefresh();
		}
	}

	/**
	 * Schedule a silent refresh N minutes before the access token expires.
	 * No periodic polling — just one precise timeout per token lifecycle.
	 */
	private scheduleTokenRefresh(): void {
		// Clear any existing schedule
		if (this.refreshTimeoutId !== null) {
			clearTimeout(this.refreshTimeoutId);
			this.refreshTimeoutId = null;
		}

		const remaining = this.getTokenRemainingTime();
		if (!remaining) return;

		const thresholdMs = TOKEN_REFRESH_THRESHOLD_MIN * 60 * 1000;
		const refreshIn = Math.max(0, remaining - thresholdMs);

		console.log(`🔄 Session: Refresh scheduled in ${Math.floor(refreshIn / 60000)} min`);

		this.refreshTimeoutId = setTimeout(() => {
			this.attemptSilentTokenRefresh();
		}, refreshIn);
	}

	// ──────────────────────────────────────────────
	// Token refresh & logout
	// ──────────────────────────────────────────────

	private handleExternalLogout(): void {
		this.clearAuthHintCookie();
		this.oauthService.logOut(false); // Don't redirect to IdP logout
		this.isAuthenticatedSubject.next(false);
		this.stopSessionMonitoring();
	}

	private attemptSilentTokenRefresh(): void {
		if (USE_LIBRARY_SILENT_REFRESH) {
			// Strategy A: Library iframe refresh (requires /silent-refresh registered in Ping)
			console.log('🔄 SR: Attempting library silent refresh (iframe)');
			this.oauthService
				.silentRefresh()
				.then(() => {
					console.log('🔄 SR: Success (iframe)');
					this.updateAuthState();
				})
				.catch((error) => {
					console.warn('🔄 SR: iframe failed, falling back to re-login', error?.message || error);
					this.manualSilentRefresh();
				});
		} else {
			// Strategy B: Manual re-authentication (no Ping redirect registration needed)
			this.manualSilentRefresh();
		}
	}

	/**
	 * Manual silent refresh: unconditionally initiates a new OIDC authorization code flow.
	 * Since the user still has a valid SSO session in Ping, the redirect completes
	 * without user interaction and produces new tokens.
	 *
	 * Note: loadDiscoveryDocumentAndLogin() short-circuits when tokens are still valid,
	 * so we use initCodeFlow() directly to always force a fresh token exchange with Ping.
	 * The callback is handled by loadDiscoveryDocumentAndTryLogin() in configureOAuth().
	 */
	private manualSilentRefresh(): void {
		console.log('🔄 SR: Attempting manual re-authentication (forced redirect)');
		this.oauthService
			.loadDiscoveryDocument()
			.then(() => {
				this.clearAuthHintCookie();
				this.oauthService.initCodeFlow();
			})
			.catch((error) => {
				console.warn('🔄 SR: Manual re-auth failed', error?.message || error);
			});
	}

	private updateAuthState(): void {
		const hasValidToken = this.oauthService.hasValidAccessToken();
		this.isAuthenticatedSubject.next(hasValidToken);
		this.authenticationEventObservable.next(hasValidToken);
	}

	// ──────────────────────────────────────────────
	// Public API
	// ──────────────────────────────────────────────

	login(): void {
		this.clearAuthHintCookie();
		this.oauthService
			.loadDiscoveryDocumentAndLogin()
			.then((result: boolean) => {
				this.authenticationEventObservable.next(result);
			})
			.catch(() => {
				this.logout();
			});
	}

	logout(): void {
		console.log('🔄 Auth: Logging out user');
		this.stopSessionMonitoring();
		this.clearAuthHintCookie();
		// Clear user data to prevent cross-session contamination
		this.addressNumberService.clear();
		this.customerService.clearCustomerData();
		this.claimsService.clearUserRegistrationData();
		void this.oauthService.revokeTokenAndLogout();
		this.isAuthenticatedSubject.next(false);
	}

	/**
	 * Force logout without trying to revoke tokens on the IdP.
	 * Used when the server already rejected our token (401) —
	 * there's no point in calling Ping to revoke something it already considers invalid.
	 * Clears local state and redirects to Ping login.
	 */
	forceLogout(): void {
		console.log('🔄 Auth: Force logout (server rejected token)');
		this.stopSessionMonitoring();
		this.clearAuthHintCookie();
		// Clear user data to prevent cross-session contamination
		this.addressNumberService.clear();
		this.customerService.clearCustomerData();
		this.claimsService.clearUserRegistrationData();
		this.isAuthenticatedSubject.next(false);
		// logOut(true) = clear local tokens AND redirect to logoutUrl (Ping SLO)
		// The SLO will redirect back to our app via TargetResource, triggering a fresh login
		this.oauthService.logOut();
	}

	getToken(): string {
		return this.oauthService.getAccessToken();
	}

	getUserClaims(): Record<string, unknown> {
		return this.oauthService.getIdentityClaims();
	}

	/**
	 * Session validity is based on the ACCESS token only.
	 * The ID token is a one-time proof of identity at login time —
	 * its short TTL (~5 min from Ping) does NOT mean the session expired.
	 */
	isAuthenticated(): boolean {
		return this.oauthService.hasValidAccessToken();
	}

	/** Alias kept for backward compatibility with AuthGuard */
	getIsAuthenticated(): boolean {
		return this.isAuthenticated();
	}

	isOAuthInitialized(): boolean {
		return !!(this.oauthService.hasValidAccessToken() || this.oauthService.getAccessToken());
	}

	// ──────────────────────────────────────────────
	// Token introspection helpers
	// ──────────────────────────────────────────────

	hasRefreshToken(): boolean {
		return !!this.oauthService.getRefreshToken();
	}

	getTokenExpirationTime(): number | null {
		if (!this.oauthService.getAccessToken()) return null;
		return this.oauthService.getAccessTokenExpiration() || null;
	}

	getTokenRemainingTime(): number | null {
		const expirationTime = this.getTokenExpirationTime();
		if (!expirationTime) return null;
		return Math.max(0, expirationTime - Date.now());
	}

	isTokenCloseToExpiring(thresholdMinutes = 5): boolean {
		const remainingTime = this.getTokenRemainingTime();
		if (!remainingTime) return false;
		return remainingTime <= thresholdMinutes * 60 * 1000;
	}

	getTokenExpirationInfo(): {
		expiresAt: Date | null;
		remainingMinutes: number | null;
		isCloseToExpiring: boolean;
	} {
		const expirationTime = this.getTokenExpirationTime();
		const remainingTime = this.getTokenRemainingTime();
		return {
			expiresAt: expirationTime ? new Date(expirationTime) : null,
			remainingMinutes: remainingTime ? Math.floor(remainingTime / (1000 * 60)) : null,
			isCloseToExpiring: this.isTokenCloseToExpiring(),
		};
	}

	getIdTokenStatus(): {
		hasToken: boolean;
		isValid: boolean;
		expiresAt: Date | null;
	} {
		const idToken = this.oauthService.getIdToken();
		const hasToken = !!idToken;
		const isValid = this.oauthService.hasValidIdToken();

		let expiresAt: Date | null = null;
		if (idToken) {
			try {
				const parts = idToken.split('.');
				if (parts.length === 3) {
					let base64Payload = parts[1];
					while (base64Payload.length % 4) base64Payload += '=';
					const payload = JSON.parse(atob(base64Payload));
					expiresAt = new Date(payload.exp * 1000);
				}
			} catch {
				/* ignore decode errors */
			}
		}
		return { hasToken, isValid, expiresAt };
	}

	getTokenStatus(): {
		accessToken: {
			hasToken: boolean;
			isValid: boolean;
			expiresAt: Date | null;
		};
		idToken: { hasToken: boolean; isValid: boolean; expiresAt: Date | null };
		refreshToken: { hasToken: boolean };
	} {
		const accessTokenExpiration = this.getTokenExpirationTime();
		return {
			accessToken: {
				hasToken: !!this.oauthService.getAccessToken(),
				isValid: this.oauthService.hasValidAccessToken(),
				expiresAt: accessTokenExpiration ? new Date(accessTokenExpiration) : null,
			},
			idToken: this.getIdTokenStatus(),
			refreshToken: { hasToken: this.hasRefreshToken() },
		};
	}

	// ──────────────────────────────────────────────
	// Auth hint cookie
	// ──────────────────────────────────────────────

	private get authCookieOptions() {
		return {
			domain: resolveCookieDomain(window.location.hostname, this.environmentService.isProduction),
			path: '/',
			sameSite: 'Lax' as const,
			secure: true,
		};
	}

	private setAuthHintCookie(): void {
		setCookie(COOKIE_NAME, 'true', this.authCookieOptions);
	}

	private clearAuthHintCookie(): void {
		deleteCookie(COOKIE_NAME, this.authCookieOptions);
	}
}
